// Copied verbatim in namat-mobile src/lib/scheduling/bookingSlots.ts (namat#44).
// Web and mobile must stay identical; change both.
//
// Booking (CONTEXT.md, «حجز»): a beneficiary picks a day, then a time; picking
// a doctor is optional. Without one, the doctor free at that time with the
// FEWEST live bookings that day is assigned, ties broken by doctor id.
//
// Everything here is pure: callers read `availabilities` and `appointments`
// from Firestore, convert Timestamps to Date, and pass them in. Days and
// session hours are LOCAL clock time, as slot generation has always been.

/** A doctor's clinic sessions, by the ids stored in availability.selectedDays. */
export const SESSION_TIMES: Record<string, { start: { hours: number; minutes: number }; end: { hours: number; minutes: number } }> = {
  session1: { start: { hours: 17, minutes: 0 }, end: { hours: 20, minutes: 0 } },
  session2: { start: { hours: 20, minutes: 0 }, end: { hours: 22, minutes: 0 } },
};

export const APPOINTMENT_DURATION_MINUTES = 15;

/** One approved `availabilities` document. selectedDays: weekday (0=Sunday) -> session ids. */
export interface AvailabilityWindow {
  coachId: string;
  startDate: Date;
  endDate: Date;
  selectedDays: Record<string, string[]>;
}

/** One `appointments` document, as far as booking cares. */
export interface BookingRecord {
  coachId: string;
  date: Date;
  status?: string | null;
}

/** A bookable time and the doctors free at it, sorted by id. */
export interface OpenSlot {
  time: Date;
  coachIds: string[];
}

export interface BookableDay {
  /** Local yyyy-mm-dd. */
  key: string;
  /** Local midnight of that day. */
  date: Date;
  slots: OpenSlot[];
}

/**
 * Cancelling writes status 'cancelled' and keeps the row; every other status,
 * and a missing one (most older rows), is a live booking.
 */
export function isLiveBooking(booking: { status?: string | null }): boolean {
  return booking?.status !== 'cancelled';
}

export function dayKey(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * The 15-minute slots of one availability window that are after `now` and not
 * in `bookedTimes` (epoch ms). Same rule the booking dialogs always used.
 */
export function generateSessionSlots(
  startDate: Date,
  endDate: Date,
  selectedDays: Record<string, string[]>,
  bookedTimes: number[],
  now: Date
): Date[] {
  const slots: Date[] = [];
  if (now.getTime() > endDate.getTime()) return slots;

  const booked = new Set(bookedTimes);
  const day = startOfLocalDay(startDate.getTime() < now.getTime() ? now : startDate);

  while (day.getTime() < endDate.getTime()) {
    for (const sessionId of selectedDays[String(day.getDay())] || []) {
      const session = SESSION_TIMES[sessionId];
      if (!session) continue;

      const slot = new Date(day);
      slot.setHours(session.start.hours, session.start.minutes, 0, 0);
      const sessionEnd = new Date(day);
      sessionEnd.setHours(session.end.hours, session.end.minutes, 0, 0);

      while (slot.getTime() < sessionEnd.getTime()) {
        if (slot.getTime() > now.getTime() && !booked.has(slot.getTime())) {
          slots.push(new Date(slot));
        }
        slot.setMinutes(slot.getMinutes() + APPOINTMENT_DURATION_MINUTES);
      }
    }
    day.setDate(day.getDate() + 1);
  }

  return slots.sort((a, b) => a.getTime() - b.getTime());
}

/** One doctor's free times across all of their windows, each time once, sorted. */
export function coachSlots(
  windows: AvailabilityWindow[],
  bookings: BookingRecord[],
  coachId: string,
  now: Date
): Date[] {
  const booked = bookings
    .filter(b => b.coachId === coachId && isLiveBooking(b))
    .map(b => b.date.getTime());

  const times = new Set<number>();
  for (const w of windows) {
    if (w.coachId !== coachId) continue;
    for (const slot of generateSessionSlots(w.startDate, w.endDate, w.selectedDays, booked, now)) {
      times.add(slot.getTime());
    }
  }
  return [...times].sort((a, b) => a - b).map(t => new Date(t));
}

/**
 * Every day with at least one free time at any of `coachIds`, earliest first;
 * each time lists the doctors free at it. Windows of doctors not in `coachIds`
 * (no longer active) are ignored.
 */
export function bookableDays(
  windows: AvailabilityWindow[],
  bookings: BookingRecord[],
  coachIds: string[],
  now: Date
): BookableDay[] {
  const freeAt = new Map<number, string[]>();
  for (const coachId of [...new Set(coachIds)].sort(byId)) {
    for (const slot of coachSlots(windows, bookings, coachId, now)) {
      const list = freeAt.get(slot.getTime());
      if (list) list.push(coachId);
      else freeAt.set(slot.getTime(), [coachId]);
    }
  }

  const days = new Map<string, BookableDay>();
  for (const time of [...freeAt.keys()].sort((a, b) => a - b)) {
    const date = new Date(time);
    const key = dayKey(date);
    let day = days.get(key);
    if (!day) {
      day = { key, date: startOfLocalDay(date), slots: [] };
      days.set(key, day);
    }
    day.slots.push({ time: date, coachIds: freeAt.get(time)! });
  }
  return [...days.values()];
}

/** The doctor's live bookings on the local day of `day`. */
export function countBookingsOnDay(bookings: BookingRecord[], coachId: string, day: Date): number {
  const key = dayKey(day);
  return bookings.filter(b => b.coachId === coachId && isLiveBooking(b) && dayKey(b.date) === key).length;
}

/**
 * The rule (namat#44): of the doctors free at the chosen time, the one with
 * the fewest live bookings that day; on a tie, the smallest doctor id.
 * null when nobody is free.
 */
export function pickLeastBookedCoach(
  freeCoachIds: string[],
  bookings: BookingRecord[],
  day: Date
): string | null {
  let best: string | null = null;
  let bestCount = Infinity;
  for (const coachId of [...freeCoachIds].sort(byId)) {
    const count = countBookingsOnDay(bookings, coachId, day);
    if (count < bestCount) {
      best = coachId;
      bestCount = count;
    }
  }
  return best;
}

// Plain code-unit order, not localeCompare, so web and every phone agree.
function byId(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
